import React, { useEffect, useMemo, useState } from "react";
import GlassPanel from "@/components/GlassPanel";
import api from "@/lib/api";
import { Calendar, MapPin, Heart, ExternalLink, Trophy, Eye, EyeOff } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { toast } from "sonner";
import { Link } from "react-router-dom";

// LIVE first, then UPCOMING, then PAST. Unknown status treated as UPCOMING.
const STATUS_ORDER = { live: 0, upcoming: 1, past: 2 };

export default function Events() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(null);
  const [showPast, setShowPast] = useState(false);

  const load = () =>
    api.get("/events").then((r) => setEvents(r.data.events || [])).catch(() => {});
  useEffect(() => { load(); }, [user?.id]);

  const toggleInterest = async (e) => {
    if (!user) {
      toast.error("Login required to register interest");
      return;
    }
    setBusy(e.id);
    try {
      const { data } = await api.post(`/events/${e.id}/interest`);
      toast.success(data.interested ? `INTERESTED IN ${e.name.toUpperCase()}` : "INTEREST REMOVED");
      setEvents((prev) => prev.map((x) => x.id === e.id
        ? { ...x, interested: data.interested, interested_count: data.count }
        : x));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(null);
    }
  };

  // LIVE → UPCOMING → PAST, optionally hiding PAST.
  const sortedEvents = useMemo(() => {
    return [...events]
      .filter((e) => showPast || (e.status || "upcoming") !== "past")
      .sort((a, b) => {
        const oa = STATUS_ORDER[a.status] ?? 1;
        const ob = STATUS_ORDER[b.status] ?? 1;
        if (oa !== ob) return oa - ob;
        // Within same bucket: soonest date first for UPCOMING/LIVE, latest first for PAST.
        const bucket = a.status || "upcoming";
        const cmp = (a.date || "").localeCompare(b.date || "");
        return bucket === "past" ? -cmp : cmp;
      });
  }, [events, showPast]);

  const pastCount = useMemo(
    () => events.filter((e) => (e.status || "upcoming") === "past").length,
    [events],
  );

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16" data-testid="events-page">
      <div className="gps-tag mb-4">COMBINES · BATTLES · COMPETITIONS</div>
      <h1 className="gps-heading text-4xl md:text-6xl">EVENTS</h1>
      <p className="text-mute max-w-2xl mt-6">
        Sanctioned GPS testing events worldwide. Gym battles, combines, qualifiers.
        {!user && <> · <Link to="/login" className="text-cyan">Log in</Link> to register interest.</>}
      </p>

      <div className="mt-8 flex items-center gap-3">
        <button
          onClick={() => setShowPast((s) => !s)}
          className={`gps-btn ${showPast ? "" : "ghost"}`}
          data-testid="events-toggle-past"
          aria-pressed={showPast}
        >
          {showPast ? <EyeOff size={14} /> : <Eye size={14} />}
          {showPast ? "HIDE PAST EVENTS" : `SHOW PAST EVENTS${pastCount ? ` (${pastCount})` : ""}`}
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-8">
        {sortedEvents.map((e) => {
          const isLive = e.status === "live";
          const isPast = e.status === "past";
          const canView = isLive || isPast;
          return (
            <GlassPanel
              key={e.id}
              className={`p-7 glow-hover ${isLive ? "ring-1 ring-cyan/40" : ""}`}
              data-testid={`event-${e.id}`}
            >
              <div className="flex items-center justify-between mb-4">
                <span
                  className={`gps-tag ${isLive ? "text-cyan animate-pulse" : ""}`}
                  data-testid={`event-status-${e.id}`}
                >
                  {isLive ? "● LIVE" : isPast ? "● PAST" : "UPCOMING"}
                </span>
                <span className="gps-mono text-xs text-mute">
                  {e.interested_count || 0} INTERESTED
                </span>
              </div>
              <div className="gps-heading text-2xl leading-tight">{e.name}</div>
              <p className="text-mute text-sm mt-3 leading-relaxed">{e.summary}</p>
              <div className="hud-divider my-5" />
              <div className="flex items-center gap-4 text-mute text-xs gps-mono">
                <span className="flex items-center gap-2"><Calendar size={12} /> {e.date}</span>
                <span className="flex items-center gap-2"><MapPin size={12} /> {e.location}</span>
              </div>
              <div className="flex gap-2 mt-5 flex-wrap">
                <button
                  onClick={() => toggleInterest(e)}
                  disabled={busy === e.id}
                  className={`gps-btn ${e.interested ? "" : "ghost"}`}
                  data-testid={`event-interest-${e.id}`}
                >
                  <Heart size={14} fill={e.interested ? "currentColor" : "none"} />
                  {e.interested ? "INTERESTED" : "I'M INTERESTED"}
                </button>
                {canView && (
                  <Link
                    to={`/events/${e.id}/leaderboard`}
                    className="gps-btn"
                    data-testid={`event-leaderboard-${e.id}`}
                  >
                    <Trophy size={14} /> VIEW LEADERBOARD
                  </Link>
                )}
                {e.registration_url && (
                  <a
                    href={e.registration_url}
                    target="_blank"
                    rel="noreferrer"
                    className="gps-btn ghost"
                    data-testid={`event-register-${e.id}`}
                  >
                    <ExternalLink size={14} /> REGISTER
                  </a>
                )}
              </div>
            </GlassPanel>
          );
        })}
        {sortedEvents.length === 0 && (
          <div className="text-mute gps-mono text-sm" data-testid="events-empty">
            {events.length === 0 ? "NO EVENTS LOADED." : "NO LIVE OR UPCOMING EVENTS. TOGGLE 'SHOW PAST EVENTS' TO REVIEW ARCHIVES."}
          </div>
        )}
      </div>
    </div>
  );
}
